/**
 * WordPress dependencies
 */
import {
	DropdownMenu,
	ToolsPanel,
	SVG,
	G,
	Line,
	Rect,
	Modal,
	Button,
	TextControl,
} from '@safe-wordpress/components';
import { _x, sprintf } from '@safe-wordpress/i18n';
import { moreVertical, edit } from '@safe-wordpress/icons';
import { useDispatch, useSelect } from '@safe-wordpress/data';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import { getElementLabel } from '@nab/utils';
import { noop } from 'lodash';
import { useState } from '@safe-wordpress/element';

export function SelectorPanel(): JSX.Element | null {
	const [ isOpen, setOpen ] = useState( false );
	const [ isLoading, setLoading ] = useState( false );
	const [ activeElement, setActiveElement ] = useActiveElement();
	if ( ! activeElement ) {
		return null;
	}

	const label = sprintf(
		'%1$s (%2$s)',
		getElementLabel( activeElement.selector ),
		activeElement.matches
	);

	return (
		<ToolsPanel
			className="nab-selector-panel"
			label={ _x( 'Selector', 'text', 'nelio-ab-testing' ) }
			resetAll={ noop }
		>
			<div className="nab-selector-panel__selector">
				<p className="nab-selector-panel__selector-label">
					<TargetIcon />
					<span>{ label }</span>
				</p>
			</div>
			<CssSelectorEditorDialog
				selector={ activeElement.selector }
				isOpen={ isOpen }
				isConfirmBusy={ isLoading }
				onCancel={ () => setOpen( false ) }
				onConfirm={ ( editedSelector ) => {
					setActiveElement( editedSelector )
						.then( () => {
							setLoading( false );
							setOpen( false );
						} )
						.catch( () => setOpen( false ) );
				} }
			/>
			<DropdownMenu
				className="nab-selector-panel__menu"
				icon={ moreVertical }
				label={ _x(
					'Select a direction',
					'command',
					'nelio-ab-testing'
				) }
				controls={ [
					{
						title: _x(
							'Edit Selector',
							'command',
							'nelio-ab-testing'
						),
						icon: edit,
						onClick: () => setOpen( true ),
					},
				] }
			/>
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

type CssSelectorEditorDialogProps = {
	readonly selector: string;
	readonly isConfirmBusy?: boolean;
	readonly isOpen?: boolean;
	readonly onCancel: () => void;
	readonly onConfirm: ( selector: string ) => void;
};
const CssSelectorEditorDialog = (
	props: CssSelectorEditorDialogProps
): JSX.Element | null => {
	const { selector, isOpen, onCancel, onConfirm, isConfirmBusy } = props;
	const [ value, setValue ] = useState( selector );

	if ( ! isOpen ) {
		return null;
	}

	return (
		<Modal
			className="nab-selector-panel__selector-dialog"
			isDismissible
			title={ _x( 'Edit Selector', 'command', 'nelio-ab-testing' ) }
			onRequestClose={ () => onCancel() }
		>
			<TextControl
				__nextHasNoMarginBottom
				__next40pxDefaultSize
				label={ _x( 'CSS Selector', 'text', 'nelio-ab-testing' ) }
				value={ value }
				onChange={ ( nextValue ) => setValue( nextValue ) }
			/>
			<div className="nab-selector-panel__selector-dialog__actions">
				<Button
					variant="secondary"
					className="nab-selector-panel__selector-dialog__cancel-action"
					onClick={ onCancel }
				>
					{ _x( 'Cancel', 'command', 'nelio-ab-testing' ) }
				</Button>
				<Button
					isBusy={ isConfirmBusy }
					disabled={ ! isValidSelector( value ) }
					variant="primary"
					className="nab-selector-panel__selector-dialog__confirm-action"
					onClick={ () => onConfirm( value ) }
				>
					{ _x( 'Save', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</Modal>
	);
};

const TargetIcon = (): JSX.Element => (
	<SVG
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		viewBox="0 0 14 14"
	>
		<G>
			<G
				transform="translate(2 2)"
				fill="none"
				stroke="#4ca3ee"
				strokeWidth="2"
			>
				<Rect width="9" height="9" stroke="none" />
				<Rect x="1" y="1" width="7" height="7" fill="none" />
			</G>
			<Line
				y2="3"
				transform="translate(6.5 -0.5)"
				fill="none"
				stroke="#4ca3ee"
				strokeWidth="1"
			/>
			<Line
				x2="3"
				transform="translate(-0.5 6.5)"
				fill="none"
				stroke="#4ca3ee"
				strokeWidth="1"
			/>
			<Line
				y2="3"
				transform="translate(6.5 10.5)"
				fill="none"
				stroke="#4ca3ee"
				strokeWidth="1"
			/>
			<Line
				x2="3"
				transform="translate(10.5 6.5)"
				fill="none"
				stroke="#4ca3ee"
				strokeWidth="1"
			/>
		</G>
	</SVG>
);

const useActiveElement = () => {
	const attrName = 'css-editor/cssEditorState';
	const editor = useSelect(
		( select ) => select( NAB_DATA ).getPageAttribute( attrName ),
		[ attrName ]
	);
	const { setPageAttribute } = useDispatch( NAB_DATA );

	const setActiveSelector = editor
		? ( activeSelector: string ) =>
				setPageAttribute( attrName, {
					...editor,
					activeSelector,
					activeElement: undefined,
				} )
		: ( _: string ) => new Promise< void >( ( r ) => r() );
	return [ editor?.activeElement, setActiveSelector ] as const;
};

function isValidSelector( selector: string ): boolean {
	const cssSelectorRegex =
		/^[a-zA-Z0-9\-\_\.\#\:\[\]\(\)\>\+\~\*\=\^\$\|\s]+$/;
	return cssSelectorRegex.test( selector );
}
